/**
 * Buscador global de la CMDB (barra superior).
 *
 * QUÉ HACE
 * ---------------------------------------------------------------------------
 * Busca elementos por nombre en TODA la empresa activa y, al elegir uno, te
 * lleva a su ficha (no al listado). Por defecto la búsqueda se acota a la
 * empresa seleccionada: el backend (`GET /api/ci/search`) filtra por
 * `company_id` del token y añade los CIs que otra empresa te haya delegado,
 * exactamente igual que el resto de la aplicación.
 *
 * CONMUTADOR «TODAS»
 * ---------------------------------------------------------------------------
 * Si el usuario administra varios tenants, puede ampliar la búsqueda a todos
 * ellos (`?scope=authorized`). Es la forma de comprobar si un equipo YA está
 * dado de alta en otra empresa antes de registrarlo y no duplicarlo en el
 * tenant equivocado. Cada resultado dice de qué empresa es y, al abrirlo, la
 * aplicación cambia de tenant y abre la ficha allí.
 *
 * Por qué reutiliza `/api/ci/search` en vez de tener endpoint propio: ya
 * conoce todos los modelos, su columna de nombre, el filtro por empresa y la
 * delegación. Aquí solo se añade la parte de «a qué pantalla voy».
 *
 * TECLADO
 *   Ctrl/Cmd + K  enfocar el buscador
 *   ↑ / ↓         moverse por los resultados
 *   Enter         abrir la ficha resaltada
 *   Esc           cerrar
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { GLOBAL_SEARCH_CI_TYPES, rankResults, searchTarget } from '../config/globalSearch';
import './GlobalSearch.css';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const PER_TYPE_LIMIT = 8;
// Una lista de 200 resultados no se lee: se muestran los más relevantes.
const MAX_VISIBLE = 40;
// Preferencia del conmutador «todas las empresas» (sobrevive al F5).
const ALL_COMPANIES_KEY = 'cmdb.gsearch.allCompanies';

export default function GlobalSearch({ companyId, companies = [], onCompanyChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // La búsqueda por defecto es de UNA empresa concreta. `all` (vista de
  // superadministrador) no es un id válido: el backend caería al company_id del
  // token y devolvería datos de otra empresa sin avisar.
  const scopeId = companyId && companyId !== 'all' ? companyId : null;

  // Conmutador «todas las empresas»: solo aparece si el usuario administra más
  // de un tenant. Sirve para comprobar dónde está ya un equipo antes de darlo
  // de alta y no registrarlo en la empresa equivocada.
  const canSearchAll = Array.isArray(companies) && companies.length > 1;
  const [allCompanies, setAllCompanies] = useState(() => {
    try {
      return localStorage.getItem(ALL_COMPANIES_KEY) === '1';
    } catch {
      return false;
    }
  });
  const searchAll = canSearchAll && allCompanies;
  // Con el conmutador activo no hace falta empresa activa: el backend busca en
  // todas las que el token tiene autorizadas.
  const canSearch = !!scopeId || searchAll;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const requestSeq = useRef(0);
  const debounceTimer = useRef(null);

  const tr = useCallback(
    (key, fallback) => {
      const value = t(key);
      return !value || value === key ? fallback : value;
    },
    [t],
  );

  /* ── Cerrar al pulsar fuera ──────────────────────────────────────────── */
  useEffect(() => {
    const onDocClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  /* ── Atajo Ctrl/Cmd + K ──────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'k') {
        event.preventDefault();
        setMobileOpen(true);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ── Búsqueda con debounce ───────────────────────────────────────────── */
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    const q = query.trim();

    if (!canSearch || q.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    setLoading(true);
    debounceTimer.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      try {
        const res = await api.ciSearch(scopeId, {
          types: GLOBAL_SEARCH_CI_TYPES.join(','),
          q,
          limit: PER_TYPE_LIMIT,
          scope: searchAll ? 'authorized' : undefined,
        });
        // Respuesta vieja: mientras se escribía ya salió otra petición.
        if (seq !== requestSeq.current) return;
        const list = Array.isArray(res?.results) ? res.results : [];
        // Solo lo que tiene ficha a la que ir.
        const usable = list.filter((item) => searchTarget(item) !== null);
        setResults(rankResults(usable, q));
        setActive(0);
        setError('');
      } catch (e) {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setError(tr('gsearch.error', 'No se pudo buscar. Reinténtalo.'));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceTimer.current);
  }, [query, scopeId, canSearch, searchAll, tr]);

  const showPanel = open && query.trim().length >= MIN_CHARS;

  // Los resultados ya vienen ordenados por relevancia: se corta por el final.
  const visible = results.slice(0, MAX_VISIBLE);
  const truncated = results.length > visible.length;

  const goTo = useCallback(
    (result) => {
      const target = searchTarget(result);
      if (!target) return;
      // Resultado de OTRA empresa → se cambia de tenant antes de navegar. Los
      // listados cargan con el `companyId` activo: sin este cambio, el
      // `?destacar=` apuntaría a un id que en la empresa actual no existe.
      const targetCompany = result.company_id != null ? String(result.company_id) : null;
      if (searchAll && targetCompany && targetCompany !== String(companyId ?? '')) {
        onCompanyChange?.(targetCompany);
      }
      setOpen(false);
      setMobileOpen(false);
      setQuery('');
      setResults([]);
      navigate(target.path, target.state ? { state: target.state } : undefined);
    },
    [navigate, searchAll, companyId, onCompanyChange],
  );

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setMobileOpen(false);
      setQuery('');
      inputRef.current?.blur();
      return;
    }
    if (!visible.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % visible.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i - 1 + visible.length) % visible.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = visible[Math.min(active, visible.length - 1)];
      if (target) goTo(target);
    }
  };

  /* Etiqueta del tipo: la traducción manda; si falta, la que manda el backend. */
  const typeLabel = useCallback(
    (result, target) => {
      const fallback = result.ci_type_label || target?.labelKey || 'Elemento';
      if (!target?.labelKey) return fallback;
      return tr(`gsearch.type.${target.labelKey}`, fallback);
    },
    [tr],
  );

  const disabled = !canSearch;
  const placeholder = disabled
    ? tr('gsearch.need_company', 'Selecciona una empresa para buscar')
    : searchAll
      ? tr('gsearch.placeholder_all', 'Buscar en todas las empresas…  (Ctrl+K)')
      : tr('gsearch.placeholder', 'Buscar en la empresa…  (Ctrl+K)');

  const groupedCount = useMemo(() => {
    const seen = new Set();
    results.forEach((r) => seen.add(r.ci_type));
    return seen.size;
  }, [results]);

  return (
    <div className={`navbar-gs ${mobileOpen ? 'is-expanded' : ''}`} ref={boxRef}>
      <div
        className={`navbar-gs-box ${showPanel ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
        onClick={() => {
          if (disabled) return;
          setMobileOpen(true);
          inputRef.current?.focus();
        }}
      >
        <svg viewBox="0 0 20 20" fill="none" className="navbar-gs-icon" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.7" />
          <path d="M13 13l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>

        <input
          ref={inputRef}
          type="text"
          className="navbar-gs-input"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={
            searchAll
              ? tr('gsearch.aria_all', 'Buscar en todas las empresas')
              : tr('gsearch.aria', 'Buscar elementos de la empresa')
          }
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck="false"
        />

        {canSearchAll && (
          <button
            type="button"
            className={`navbar-gs-scope ${searchAll ? 'is-on' : ''}`}
            aria-pressed={searchAll}
            title={
              searchAll
                ? tr(
                    'gsearch.scope_all_off',
                    'Buscando en todas las empresas · pulsa para acotar a la activa',
                  )
                : tr(
                    'gsearch.scope_all_on',
                    'Buscar también en el resto de empresas que administras',
                  )
            }
            onClick={(e) => {
              e.stopPropagation();
              // Los resultados viejos son de otro alcance: no se mezclan.
              setResults([]);
              setError('');
              setAllCompanies((prev) => {
                const next = !prev;
                try {
                  localStorage.setItem(ALL_COMPANIES_KEY, next ? '1' : '0');
                } catch {
                  /* sin localStorage: solo dura esta sesión */
                }
                return next;
              });
              setOpen(true);
              inputRef.current?.focus();
            }}
          >
            <span aria-hidden="true">🌐</span>
            <span className="navbar-gs-scope-label">{tr('gsearch.scope_all', 'Todas')}</span>
          </button>
        )}

        {query && (
          <button
            type="button"
            className="navbar-gs-clear"
            title={tr('gsearch.clear', 'Limpiar')}
            onClick={(e) => {
              e.stopPropagation();
              setQuery('');
              setResults([]);
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
        {loading && <span className="navbar-gs-spinner" aria-hidden="true" />}
      </div>

      {showPanel && (
        <div className="navbar-gs-panel" role="listbox">
          {error && <div className="navbar-gs-empty is-error">{error}</div>}

          {!error && !loading && results.length === 0 && (
            <div className="navbar-gs-empty">
              {searchAll
                ? tr('gsearch.no_results_all', 'Nada por ese nombre en ninguna de tus empresas.')
                : tr('gsearch.no_results', 'Nada por ese nombre en esta empresa.')}
            </div>
          )}

          {results.length > 0 && (
            <>
              <div className="navbar-gs-head">
                <span>
                  {searchAll && (
                    <span className="navbar-gs-head-scope">
                      🌐 {tr('gsearch.all_companies', 'todas las empresas')}
                    </span>
                  )}
                  {truncated ? `${visible.length}/${results.length} ` : `${results.length} `}
                  {results.length === 1
                    ? tr('gsearch.result_one', 'resultado')
                    : tr('gsearch.result_many', 'resultados')}
                </span>
                <span className="navbar-gs-head-hint">
                  {tr('gsearch.hint', '↑↓ para moverte · Enter para abrir la ficha')}
                </span>
              </div>
              <div className="navbar-gs-list">
                {visible.map((result, index) => {
                  const target = searchTarget(result);
                  return (
                    <button
                      key={`${result.ci_type}-${result.ci_id}`}
                      type="button"
                      role="option"
                      aria-selected={index === active}
                      className={`navbar-gs-item ${index === active ? 'is-active' : ''}`}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => goTo(result)}
                    >
                      <span className="navbar-gs-item-icon" aria-hidden="true">
                        {target?.icon || '•'}
                      </span>
                      <span className="navbar-gs-item-body">
                        <span className="navbar-gs-item-name">{result.name || '—'}</span>
                        <span className="navbar-gs-item-meta">
                          <span className="navbar-gs-item-type">{typeLabel(result, target)}</span>
                          {result.description && (
                            <span className="navbar-gs-item-desc">{result.description}</span>
                          )}
                        </span>
                      </span>
                      {searchAll && result.company_name && (
                        <span
                          className={`navbar-gs-item-company ${
                            String(result.company_id) === String(companyId) ? 'is-current' : ''
                          }`}
                          title={
                            String(result.company_id) === String(companyId)
                              ? tr('gsearch.here', 'Ya estás en esta empresa')
                              : tr(
                                  'gsearch.switch_hint',
                                  'Al abrirla se cambiará a esta empresa',
                                )
                          }
                        >
                          {result.company_name}
                        </span>
                      )}
                      <span className="navbar-gs-item-go" aria-hidden="true">↵</span>
                    </button>
                  );
                })}
              </div>
              <div className="navbar-gs-foot">
                {searchAll
                  ? tr(
                      'gsearch.scope_all_foot',
                      'En todas tus empresas · al abrir un resultado de otro tenant se cambia de empresa',
                    )
                  : tr('gsearch.scope', 'Solo elementos de la empresa activa')}
                {groupedCount > 1
                  ? ` · ${groupedCount} ${tr('gsearch.types', 'tipos')}`
                  : ''}
                {truncated ? ` · ${tr('gsearch.refine', 'afina la búsqueda para ver el resto')}` : ''}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
